import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Home, ArrowLeft } from "lucide-react";

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const ErrorNumber = styled.h1`
  font-size: 8rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 6rem;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: ${({ theme }) => theme.spacing.md} 0;
  font-weight: 600;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const ErrorMessage = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 500px;
  line-height: 1.6;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  justify-content: center;

  ${({ variant, theme }) =>
    variant === "primary"
      ? `
        background-color: ${theme.colors.primary};
        color: white;

        &:hover {
          background-color: ${theme.colors.primaryDark};
          transform: translateY(-2px);
        }
      `
      : `
        background-color: transparent;
        color: ${theme.colors.text.secondary};
        border: 1px solid ${theme.colors.border.light};

        &:hover {
          background-color: ${theme.colors.background};
          color: ${theme.colors.text.primary};
          border-color: ${theme.colors.border.dark};
        }
      `}

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const IllustrationContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  opacity: 0.1;
  font-size: 4rem;
`;

export const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <NotFoundContainer>
      <IllustrationContainer>🔍</IllustrationContainer>

      <ErrorNumber>404</ErrorNumber>

      <ErrorTitle>Página não encontrada</ErrorTitle>

      <ErrorMessage>
        Ops! A página que você está procurando não existe ou foi movida para
        outro local. Verifique o endereço digitado ou navegue de volta para a
        página inicial.
      </ErrorMessage>

      <ButtonGroup>
        <Button variant="primary" onClick={handleGoHome}>
          <Home size={20} />
          Ir para o Início
        </Button>

        <Button variant="secondary" onClick={handleGoBack}>
          <ArrowLeft size={20} />
          Voltar
        </Button>
      </ButtonGroup>
    </NotFoundContainer>
  );
};
